import {
  API_ROUTES,
  type AddNoteRequest,
  type ListReviewQueueQuery,
  reviewItemDetailSchema,
  reviewNoteSchema,
  reviewQueueItemSchema,
  reviewStatsSchema,
  type SaveDraftRequest,
  type SubmitReviewRequest,
  supplierToTeachSchema,
  teachSupplierResultSchema,
  type TeachSupplierRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/**
 * Review queries.
 *
 * The queue is the screen a bookkeeper lives in all morning, so it is kept
 * warm: a short stale time, and the detail for the next document prefetched as
 * soon as the current one is open. Pressing J should feel like nothing
 * happened, not like a page load.
 */

export const reviewKeys = {
  all: (organizationId: string) => ['review', organizationId] as const,
  queue: (organizationId: string, filters: Partial<ListReviewQueueQuery>) =>
    ['review', organizationId, 'queue', filters] as const,
  item: (organizationId: string, itemId: string) =>
    ['review', organizationId, 'item', itemId] as const,
  stats: (organizationId: string) => ['review', organizationId, 'stats'] as const,
} as const;

const queuePageSchema = z.object({
  items: z.array(reviewQueueItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

function toSearch(filters: Partial<ListReviewQueueQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

export const reviewQueueQuery = (
  organizationId: string,
  filters: Partial<ListReviewQueueQuery> = {},
) =>
  queryOptions({
    queryKey: reviewKeys.queue(organizationId, filters),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.review.list(organizationId)}${toSearch(filters)}`,
        schema: queuePageSchema,
        signal,
      }),
    // Documents land continuously behind the reviewer as extraction completes.
    staleTime: 15_000,
  });

export const reviewItemQuery = (organizationId: string, itemId: string) =>
  queryOptions({
    queryKey: reviewKeys.item(organizationId, itemId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.review.byId(organizationId, itemId),
        schema: reviewItemDetailSchema,
        signal,
      }),
  });

export const reviewStatsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: reviewKeys.stats(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.review.stats(organizationId),
        schema: reviewStatsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

/** Starts the item's clock. POST because it changes state. */
export function openReviewItem(organizationId: string, itemId: string) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.review.open(organizationId, itemId),
    schema: z.object({ draftVersion: z.number().int(), startedAt: z.string() }),
  });
}

export function saveReviewDraft(organizationId: string, itemId: string, body: SaveDraftRequest) {
  return apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.review.draft(organizationId, itemId),
    schema: z.object({ version: z.number().int() }),
    body,
  });
}

export function submitReview(organizationId: string, itemId: string, body: SubmitReviewRequest) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.review.submit(organizationId, itemId),
    schema: z.object({ decisionId: z.uuid(), correctionsRecorded: z.number().int() }),
    body,
  });
}

export function addReviewNote(organizationId: string, itemId: string, body: AddNoteRequest) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.review.notes(organizationId, itemId),
    schema: reviewNoteSchema,
    body,
  });
}

/**
 * Clear every duplicate in one action.
 *
 * Strict test only, so nothing ambiguous disappears: same supplier, same
 * invoice number, same total as a document already here.
 */
export function setAsideDuplicates(organizationId: string) {
  return apiClient.request({
    path: API_ROUTES.review.setAsideDuplicates(organizationId),
    method: 'POST',
    body: {},
    schema: z.object({ setAside: z.number() }),
  });
}

/** File everything the engine is certain about, as drafts. */
export function fileCertainDocuments(organizationId: string) {
  return apiClient.request({
    path: API_ROUTES.review.fileCertain(organizationId),
    method: 'POST',
    body: {},
    schema: z.object({
      filed: z.number(),
      failed: z.number(),
      firstError: z.string().nullable(),
    }),
  });
}

/** Waiting documents grouped by a supplier nobody has decided about yet. */
export const suppliersToTeachQuery = (organizationId: string) =>
  queryOptions({
    queryKey: [...reviewKeys.all(organizationId), 'suppliers'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.review.suppliersToTeach(organizationId),
        schema: z.array(supplierToTeachSchema),
        signal,
      }),
    staleTime: 10_000,
  });

export function teachSupplier(organizationId: string, body: TeachSupplierRequest) {
  return apiClient.request({
    path: API_ROUTES.review.suppliersToTeach(organizationId),
    method: 'POST',
    body,
    schema: teachSupplierResultSchema,
  });
}
